import { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { Input } from "../../components/Input";
import { MoneyInput } from "../../components/MoneyInput";
import { MoneyConfirmationPanel } from "../../components/MoneyConfirmationPanel";
import { Sheet } from "../../components/Sheet";
import { MoreVerticalIcon, PencilIcon } from "../../components/icons";
import { ApiError } from "../../lib/api";
import { formatARSExact, getArsAmountPolicy, inputValueFromCents, parseArsInput } from "../../lib/money";
import type { CorrectMovementInput, Movement, MovementAuditEvent } from "./api";
import {
  useCorrectMovement,
  useMovement,
  useMovementConcepts,
  useMovements,
  useReverseMovement,
  useUpdateMovementNote,
  useVoidMovement,
} from "./hooks";
import { usePaymentMethods } from "../payment-methods/hooks";

type Action = "note" | "correct" | "void" | "reverse" | null;

function dayBounds(date: string) {
  return {
    from: `${date}T00:00:00.000Z`,
    to: `${date}T23:59:59.999Z`,
  };
}

function movementLabel(movement: Pick<Movement, "type" | "concept" | "freeformConcept">) {
  return movement.concept?.name ?? movement.freeformConcept ?? (movement.type === "income" ? "Ingreso" : "Egreso");
}

function statusOf(movement: Pick<Movement, "status">) {
  return movement.status ?? "active";
}

export function MovementsHistoryPanel({ date }: { date: string }) {
  const [type, setType] = useState<"all" | "income" | "expense">("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [requestedAction, setRequestedAction] = useState<Action>(null);
  const [actionMenuId, setActionMenuId] = useState<number | null>(null);
  const params = { ...dayBounds(date), ...(type === "all" ? {} : { type }), limit: 200 } as const;
  const movements = useMovements(params);
  const active = (movements.data ?? []).filter((movement) => statusOf(movement) === "active");
  const income = active.filter((movement) => movement.type === "income").reduce((sum, movement) => sum + movement.amountCents, 0);
  const expense = active.filter((movement) => movement.type === "expense").reduce((sum, movement) => sum + movement.amountCents, 0);

  return (
    <>
      <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", marginBottom: "var(--space-3)" }}>
        <Chip variant="option" size="md" active={type === "all"} onClick={() => setType("all")}>Todos</Chip>
        <Chip variant="option" size="md" active={type === "income"} onClick={() => setType("income")}>Ingresos</Chip>
        <Chip variant="option" size="md" active={type === "expense"} onClick={() => setType("expense")}>Egresos</Chip>
      </div>
      <div className="history-kpis">
        <span><small>Ingresos activos</small><strong className="num">{formatARSExact(income)}</strong></span>
        <span><small>Egresos activos</small><strong className="num">{formatARSExact(expense)}</strong></span>
      </div>
      {movements.isLoading && <div className="skeleton-list"><span /><span /><span /></div>}
      {movements.data?.length === 0 && <div className="empty-state"><strong>Sin movimientos</strong><span>No se registraron ingresos ni egresos manuales en esta fecha.</span></div>}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {movements.data?.map((movement) => {
          const voided = statusOf(movement) === "voided";
          return (
            <article
              key={movement.id}
              style={{
                display: "grid", gridTemplateColumns: "1fr auto", gap: "var(--space-3)", textAlign: "left", width: "100%",
                padding: "var(--space-3) var(--space-4)", border: "1px solid var(--border-default)", borderRadius: "var(--radius-md)",
                background: "var(--surface-raised)", color: "var(--text-primary)", opacity: voided ? 0.62 : 1,
              }}
            >
              <button
                type="button"
                onClick={() => { setActionMenuId(null); setRequestedAction(null); setSelectedId(movement.id); }}
                style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, padding: 0, border: 0, background: "transparent", color: "inherit", font: "inherit", textAlign: "left", cursor: "pointer" }}
              >
                <span style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
                  <strong>{movementLabel(movement)}</strong>
                  <Chip variant={movement.type === "income" ? "tag-success" : "tag-danger"} size="sm">{movement.type === "income" ? "Ingreso" : "Egreso"}</Chip>
                  {voided && <Chip variant="tag-danger" size="sm">Anulado</Chip>}
                </span>
                <small style={{ color: "var(--text-secondary)" }}>
                  {new Date(movement.occurredAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                  {movement.paymentMethod ? ` · ${movement.paymentMethod.name}` : ""}
                </small>
                {movement.note && <small style={{ color: "var(--text-secondary)" }}>Observación: {movement.note}</small>}
              </button>
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between", gap: "var(--space-2)" }}>
                <strong className="num" style={{ color: movement.type === "expense" ? "var(--color-danger)" : "var(--text-primary)" }}>
                  {movement.type === "expense" ? "−" : "+"}{formatARSExact(movement.amountCents)}
                </strong>
                {!voided && <span style={{ display: "flex", gap: "var(--space-1)", position: "relative" }}>
                  <button
                    className="icon-action"
                    type="button"
                    aria-label={`Corregir ${movementLabel(movement)}`}
                    title="Corregir movimiento"
                    onClick={(event) => { event.stopPropagation(); setActionMenuId(null); setRequestedAction("correct"); setSelectedId(movement.id); }}
                  ><PencilIcon /></button>
                  <button
                    className="icon-action"
                    type="button"
                    aria-label="Más acciones"
                    aria-haspopup="menu"
                    aria-expanded={actionMenuId === movement.id}
                    title="Más acciones"
                    onClick={(event) => { event.stopPropagation(); setActionMenuId((current) => current === movement.id ? null : movement.id); }}
                  ><MoreVerticalIcon /></button>
                  {actionMenuId === movement.id && <div
                    role="menu"
                    onClick={(event) => event.stopPropagation()}
                    style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 2, width: 230, padding: "var(--space-1)", border: "1px solid var(--border-default)", borderRadius: "var(--radius-md)", background: "var(--surface-raised)", boxShadow: "var(--shadow-md)" }}
                  >
                    <button type="button" role="menuitem" style={menuItemStyle} onClick={() => { setActionMenuId(null); setRequestedAction("reverse"); setSelectedId(movement.id); }}>
                      {movement.type === "expense" ? "Registrar reintegro" : "Registrar salida posterior"}
                    </button>
                    <button type="button" role="menuitem" style={{ ...menuItemStyle, color: "var(--color-danger)" }} onClick={() => { setActionMenuId(null); setRequestedAction("void"); setSelectedId(movement.id); }}>
                      Anular registro
                    </button>
                  </div>}
                </span>}
              </span>
            </article>
          );
        })}
      </div>
      <MovementDetailSheet id={selectedId} initialAction={requestedAction} onClose={() => { setSelectedId(null); setRequestedAction(null); }} />
    </>
  );
}

function MovementDetailSheet({ id, initialAction, onClose }: { id: number | null; initialAction: Action; onClose: () => void }) {
  const detail = useMovement(id, id != null);
  const [action, setAction] = useState<Action>(initialAction);
  useEffect(() => setAction(initialAction), [id, initialAction]);
  const movement = detail.data;
  const voided = movement ? statusOf(movement) === "voided" : false;

  if (id == null) return null;
  return (
    <Sheet open onClose={onClose} title={action ? actionTitle(action, movement) : "Detalle del movimiento"}>
      {detail.isLoading && <div className="skeleton-list"><span /><span /></div>}
      {detail.error && <p className="form-error">No se pudo cargar el movimiento.</p>}
      {movement && !action && <>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)", alignItems: "start" }}>
            <div><strong>{movementLabel(movement)}</strong><p style={muted}>{new Date(movement.occurredAt).toLocaleString("es-AR")}</p></div>
            <strong className="num" style={{ fontSize: "var(--font-size-22)", color: movement.type === "expense" ? "var(--color-danger)" : "var(--text-primary)" }}>{formatARSExact(movement.amountCents)}</strong>
          </div>
          <dl style={detailsStyle}>
            <div><dt>Tipo</dt><dd>{movement.type === "income" ? "Ingreso" : "Egreso"}</dd></div>
            <div><dt>Medio</dt><dd>{movement.paymentMethod?.name ?? "Sin especificar"}</dd></div>
            <div><dt>Operador</dt><dd>#{movement.operatorId}</dd></div>
            <div><dt>Estado</dt><dd>{voided ? "Anulado" : "Activo"}</dd></div>
          </dl>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-1)" }}>
            <p style={{ ...muted, margin: 0 }}>Observación: {movement.note ?? "Sin observación"}</p>
            {!voided && <button
              className="icon-action"
              type="button"
              aria-label="Editar observación"
              title="Editar observación"
              onClick={() => setAction("note")}
            ><PencilIcon /></button>}
          </div>
          {voided && movement.voidReason && <p style={{ ...muted, margin: 0 }}>Motivo de anulación: {movement.voidReason}</p>}
          {movement.supersedesMovementId && <p style={muted}>Corrige el movimiento #{movement.supersedesMovementId}.</p>}
          {movement.reversalOfMovementId && <p style={muted}>Revierte el movimiento #{movement.reversalOfMovementId}.</p>}
          {movement.reversedByMovementId && <p style={muted}>Fue revertido por el movimiento #{movement.reversedByMovementId}.</p>}
          {(movement.auditEvents?.length ?? 0) > 0 && <AuditTrail events={movement.auditEvents!} />}
        </div>
      </>}
      {movement && action === "note" && <NoteForm movement={movement} onDone={() => setAction(null)} />}
      {movement && action === "correct" && <CorrectionForm movement={movement} onDone={onClose} />}
      {movement && (action === "void" || action === "reverse") && <ReasonForm movement={movement} action={action} onDone={onClose} />}
    </Sheet>
  );
}

function NoteForm({ movement, onDone }: { movement: Movement; onDone: () => void }) {
  const [note, setNote] = useState(movement.note ?? "");
  const [error, setError] = useState<string | null>(null);
  const mutation = useUpdateMovementNote();
  return <div style={formStyle}>
    <p style={muted}>Solo se modifica la nota; el importe y los datos económicos no cambian.</p>
    <Input label="Nota" value={note} onChange={(event) => { setNote(event.target.value); setError(null); }} maxLength={500} error={error ?? undefined} autoFocus />
    {error && <p className="form-error">{error}</p>}
    <Button fullWidth size="lg" disabled={mutation.isPending} onClick={async () => {
      try { await mutation.mutateAsync({ id: movement.id, body: { note: note.trim() || null } }); onDone(); }
      catch (cause) { setError((cause as Error).message); }
    }}>{mutation.isPending ? "Guardando…" : "Guardar nota"}</Button>
  </div>;
}

function ReasonForm({ movement, action, onDone }: { movement: Movement; action: "void" | "reverse"; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmationToken, setConfirmationToken] = useState<string | null>(null);
  const mutation = action === "void" ? useVoidMovement() : useReverseMovement();
  const reverseLabel = movement.type === "expense" ? "registrar reintegro" : "registrar salida posterior";
  const verb = action === "void" ? "anular" : reverseLabel;
  const submit = async (moneyConfirmationToken?: string) => {
    if (!reason.trim()) { setError("El motivo es obligatorio."); return; }
    try {
      await mutation.mutateAsync({
        id: movement.id,
        body: action === "void" ? { reason: reason.trim() } : { reason: reason.trim(), ...(moneyConfirmationToken ? { moneyConfirmationToken } : {}) },
      });
      onDone();
    } catch (cause) {
      const confirmation = action === "reverse" ? getMoneyConfirmation(cause) : null;
      if (confirmation) setConfirmationToken(confirmation.token);
      else setError((cause as Error).message);
    }
  };
  if (confirmationToken) {
    return <MoneyConfirmationPanel
      amountCents={movement.amountCents}
      actionLabel={movement.type === "expense" ? "Confirmar reintegro" : "Confirmar salida"}
      pending={mutation.isPending}
      onConfirm={() => void submit(confirmationToken)}
      onCancel={() => setConfirmationToken(null)}
    />;
  }
  return <div style={formStyle}>
    <p style={muted}>{action === "void" ? "Usalo si el movimiento nunca debió existir. Dejará de afectar los totales." : "Usalo cuando el movimiento ocurrió y luego se deshizo. Se registrará un nuevo movimiento contrario."}</p>
    <Input label="Motivo" value={reason} onChange={(event) => { setReason(event.target.value); setError(null); }} maxLength={500} autoFocus error={error ?? undefined} />
    {error && <p className="form-error">{error}</p>}
    <Button variant={action === "void" ? "danger" : "warning"} fullWidth size="lg" disabled={mutation.isPending} onClick={() => void submit()}>{mutation.isPending ? "Confirmando…" : `Confirmar ${verb}`}</Button>
  </div>;
}

function CorrectionForm({ movement, onDone }: { movement: Movement; onDone: () => void }) {
  const [type, setType] = useState<"income" | "expense">(movement.type);
  const [amount, setAmount] = useState(inputValueFromCents(movement.amountCents));
  const [conceptId, setConceptId] = useState<number | null>(movement.conceptId);
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(movement.paymentMethodId);
  const [occurredOn, setOccurredOn] = useState(() => movement.occurredAt.slice(0, 10));
  const [note, setNote] = useState(movement.note ?? "");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<{ body: CorrectMovementInput; token: string; amountCents: number } | null>(null);
  const concepts = useMovementConcepts(type);
  const paymentMethods = usePaymentMethods();
  const mutation = useCorrectMovement();
  const activeConcepts = useMemo(() => (concepts.data ?? []).filter((concept) => concept.active), [concepts.data]);

  useEffect(() => {
    if (conceptId != null && !activeConcepts.some((concept) => concept.id === conceptId)) setConceptId(null);
  }, [activeConcepts, conceptId]);

  const submit = async (body: CorrectMovementInput) => {
    try {
      await mutation.mutateAsync({ id: movement.id, body });
      onDone();
    } catch (cause) {
      const confirmation = getMoneyConfirmation(cause);
      if (confirmation) {
        setPendingConfirmation({ body, token: confirmation.token, amountCents: confirmation.amountCents ?? body.amountCents });
      } else {
        setError((cause as Error).message);
      }
    }
  };

  if (pendingConfirmation) {
    return <MoneyConfirmationPanel
      amountCents={pendingConfirmation.amountCents}
      actionLabel="Confirmar corrección"
      pending={mutation.isPending}
      onConfirm={() => void submit({ ...pendingConfirmation.body, moneyConfirmationToken: pendingConfirmation.token })}
      onCancel={() => setPendingConfirmation(null)}
    />;
  }

  return <div style={formStyle}>
    <p style={muted}>La corrección anula este registro y crea uno nuevo vinculado. El original se conserva en el historial.</p>
    <div style={{ display: "flex", gap: "var(--space-2)" }}>
      <Chip variant="option" size="md" active={type === "income"} onClick={() => setType("income")}>Ingreso</Chip>
      <Chip variant="option" size="md" active={type === "expense"} onClick={() => setType("expense")}>Egreso</Chip>
    </div>
    <MoneyInput label="Importe corregido" value={amount} onChange={(event) => { setAmount(event.target.value); setError(null); }} error={error ?? undefined} />
    <label className="field-control"><span>Concepto</span><select value={conceptId ?? ""} onChange={(event) => setConceptId(event.target.value ? Number(event.target.value) : null)}><option value="">Elegir concepto…</option>{activeConcepts.map((concept) => <option key={concept.id} value={concept.id}>{concept.name}</option>)}</select></label>
    <label className="field-control"><span>Medio de pago</span><select value={paymentMethodId ?? ""} onChange={(event) => setPaymentMethodId(event.target.value ? Number(event.target.value) : null)}><option value="">Sin especificar</option>{(paymentMethods.data ?? []).filter((method) => method.active).map((method) => <option key={method.id} value={method.id}>{method.name}</option>)}</select></label>
    <label className="field-control"><span>Fecha del movimiento</span><input type="date" value={occurredOn} onChange={(event) => setOccurredOn(event.target.value)} /></label>
    <Input label="Nota" value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} />
    <Input label="Motivo de la corrección" value={reason} onChange={(event) => { setReason(event.target.value); setError(null); }} maxLength={500} />
    {error && <p className="form-error">{error}</p>}
    <Button fullWidth size="lg" disabled={mutation.isPending} onClick={async () => {
      const parsed = parseArsInput(amount);
      if (!parsed.ok) { setError(parsed.message); return; }
      if (parsed.cents <= 0) { setError("El importe tiene que ser mayor a 0."); return; }
      if (getArsAmountPolicy(parsed.cents) === "blocked") { setError("El importe supera el máximo permitido."); return; }
      if (conceptId == null) { setError("Elegí un concepto."); return; }
      if (!reason.trim()) { setError("El motivo es obligatorio."); return; }
      if (!occurredOn) { setError("Ingresá la fecha del movimiento."); return; }
      await submit({ type, amountCents: parsed.cents, conceptId, paymentMethodId, note: note.trim() || null, occurredAt: new Date(`${occurredOn}T12:00:00`).toISOString(), reason: reason.trim() });
    }}>{mutation.isPending ? "Corrigiendo…" : "Confirmar corrección"}</Button>
  </div>;
}

function AuditTrail({ events }: { events: MovementAuditEvent[] }) {
  return <section style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: "var(--space-3)" }}><strong>Auditoría</strong><div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>{events.map((event) => <div key={event.id} style={{ fontSize: "var(--font-size-13)", color: "var(--text-secondary)" }}><strong style={{ color: "var(--text-primary)" }}>{auditLabel(event.action)}</strong> · {new Date(event.createdAt).toLocaleString("es-AR")} · operador #{event.operatorId}{event.reason ? ` · ${event.reason}` : ""}</div>)}</div></section>;
}

function actionTitle(action: Exclude<Action, null>, movement?: Movement) {
  if (action === "reverse") return movement?.type === "expense" ? "Registrar reintegro" : "Registrar salida posterior";
  return ({ note: "Editar observación", correct: "Corregir movimiento", void: "Anular registro" })[action];
}
function auditLabel(action: string) { return ({ created: "Creado", metadata_updated: "Nota editada", corrected: "Corregido", voided: "Anulado", reversed: "Reversado" })[action] ?? action; }

function getMoneyConfirmation(error: unknown): { token: string; amountCents?: number } | null {
  if (!(error instanceof ApiError) || error.status !== 422 || !error.body || typeof error.body !== "object") return null;
  const body = error.body as { error?: unknown; confirmationToken?: unknown; warnings?: Array<{ amountCents?: unknown }> };
  if (body.error !== "money_confirmation_required" || typeof body.confirmationToken !== "string") return null;
  const amount = body.warnings?.[0]?.amountCents;
  return { token: body.confirmationToken, amountCents: typeof amount === "number" ? amount : undefined };
}

const muted: React.CSSProperties = { margin: 0, color: "var(--text-secondary)", fontSize: "var(--font-size-14)", lineHeight: 1.45 };
const formStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "var(--space-3)" };
const detailsStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)", margin: 0, fontSize: "var(--font-size-14)" };
const menuItemStyle: React.CSSProperties = { display: "block", width: "100%", padding: "10px var(--space-2)", border: 0, borderRadius: "var(--radius-sm)", background: "transparent", color: "var(--text-primary)", textAlign: "left", font: "inherit", fontSize: "var(--font-size-14)", cursor: "pointer" };
